import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { createApi, type EnvName } from "./api";
import { executeVoicePlan } from "./executeVoicePlan";
import {
  applyCommands,
  parseVoiceCommands,
  sectionCodeFromPath,
  type SectionHandle,
  type UiSnapshot,
} from "./voiceDispatch";

type VoiceControlValue = {
  env: EnvName;
  uiRevision: number;
  voiceExecuting: boolean;
  notifyUiChange: () => void;
  registerHandle: (
    env: EnvName,
    sectionCode: string,
    handle: SectionHandle,
  ) => void;
  unregisterHandle: (env: EnvName, sectionCode: string) => void;
  /** Voice: REST submit all sections. Weather/debug: local mounted handle only. */
  applyCommands: (raw: unknown) => Promise<void>;
  applyLocalCommands: (raw: unknown) => Promise<void>;
  getUiContext: () => UiSnapshot;
  clearVoiceSession: () => void;
};

const VoiceControlContext = createContext<VoiceControlValue | null>(null);

function envFromPath(pathname: string): EnvName {
  return pathname.startsWith("/sim") ? "sim" : "live";
}

export function emptyUiSnapshot(route: string): UiSnapshot {
  const mounted = sectionCodeFromPath(route);
  return {
    route,
    section_code: mounted ?? "",
    answers: {},
    outlook_48h: {},
    tier: null,
    visible_question_ids: [],
  };
}

function warnRejected(rejected: unknown[]) {
  if (rejected.length > 0 && import.meta.env.DEV) {
    console.warn("Voice commands rejected:", rejected);
  }
}

export function VoiceControlProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const env = envFromPath(location.pathname);
  const api = useMemo(() => createApi(env), [env]);

  const handleRef = useRef<{
    env: EnvName;
    code: string;
    handle: SectionHandle;
  } | null>(null);
  const pendingRef = useRef<import("./voiceDispatch").VoiceCommandWire[]>([]);
  const [uiRevision, setUiRevision] = useState(0);
  const [voiceExecuting, setVoiceExecuting] = useState(false);

  const notifyUiChange = useCallback(() => {
    setUiRevision((v) => v + 1);
  }, []);

  const clearVoiceSession = useCallback(() => {
    pendingRef.current = [];
  }, []);

  const getHandle = useCallback(
    (sectionCode: string): SectionHandle | null => {
      const active = handleRef.current;
      if (!active || active.env !== env) return null;
      if (active.code !== sectionCode.trim().toUpperCase()) return null;
      return active.handle;
    },
    [env],
  );

  const queuePending = useCallback(
    (cmds: import("./voiceDispatch").VoiceCommandWire[]) => {
      pendingRef.current = [...pendingRef.current, ...cmds];
    },
    [],
  );

  const applyLocalCommands = useCallback(
    async (raw: unknown) => {
      const { commands: parsed, rejected } = parseVoiceCommands(raw);
      warnRejected(rejected);
      await applyCommands(parsed, {
        env,
        navigate,
        getHandle,
        queuePending,
      });
    },
    [env, navigate, getHandle, queuePending],
  );

  const dispatch = useCallback(
    async (raw: unknown) => {
      const { commands: parsed, rejected } = parseVoiceCommands(raw);
      warnRejected(rejected);
      setVoiceExecuting(true);
      try {
        await executeVoicePlan(parsed, { env, api, qc, navigate, getHandle });
      } finally {
        setVoiceExecuting(false);
      }
    },
    [env, api, qc, navigate, getHandle],
  );

  const registerHandle = useCallback(
    (handleEnv: EnvName, sectionCode: string, handle: SectionHandle) => {
      handleRef.current = { env: handleEnv, code: sectionCode.trim().toUpperCase(), handle };
    },
    [],
  );

  const unregisterHandle = useCallback(
    (handleEnv: EnvName, sectionCode: string) => {
      const code = sectionCode.trim().toUpperCase();
      const active = handleRef.current;
      if (active && active.env === handleEnv && active.code === code) {
        handleRef.current = null;
      }
    },
    [],
  );

  const getUiContext = useCallback((): UiSnapshot => {
    const active = handleRef.current;
    if (active && active.env === env) {
      return active.handle.snapshot();
    }
    return emptyUiSnapshot(location.pathname);
  }, [env, location.pathname]);

  const value = useMemo(
    () => ({
      env,
      uiRevision,
      voiceExecuting,
      notifyUiChange,
      registerHandle,
      unregisterHandle,
      applyCommands: dispatch,
      applyLocalCommands,
      getUiContext,
      clearVoiceSession,
    }),
    [
      env,
      uiRevision,
      voiceExecuting,
      notifyUiChange,
      registerHandle,
      unregisterHandle,
      dispatch,
      applyLocalCommands,
      getUiContext,
      clearVoiceSession,
    ],
  );

  return (
    <VoiceControlContext.Provider value={value}>
      {children}
    </VoiceControlContext.Provider>
  );
}

export function useVoiceControl() {
  const ctx = useContext(VoiceControlContext);
  if (!ctx) {
    throw new Error("useVoiceControl must be used within VoiceControlProvider");
  }
  return ctx;
}
